import { gql } from "@apollo/client";
import { OrderBy } from "../queryString";
import { RegisteredVehicle, registeredVehicleFragment } from "../fragments";

export const GET_PAYEE_TRANSACTIONS_PRICE_FILTER = { price: { _neq: 0 } };
export const GET_PAYEE_TRANSACTIONS_POTENTIAL_REVENUE_FILTER = { potential_revenue: { _neq: 0 } };

export const GET_CURBSUITE_TRANSACTIONS = gql`
  query GetCurbsuiteTransactions(
    $since: date!
    $until: date!
    $transactionType: String!
    $priceFilter: financial_transaction_log_no_balance_bool_exp!
    $filter: financial_transaction_log_no_balance_bool_exp!
    $limit: Int!
    $offset: Int!
    $orderBy: [financial_transaction_log_no_balance_order_by!]
  ) {
    transactions: financial_transaction_log_no_balance(
      where: {
        transaction_date: { _gte: $since, _lt: $until }
        transaction_type: { _eq: $transactionType }
        _or: [{ first_park: { _eq: true } }, $priceFilter]
        _and: [$filter]
      }
      limit: $limit
      offset: $offset
      order_by: $orderBy
    ) {
      transactionId: transaction_id
      firstPark: first_park
      unregisteredVehicleViolation: unregistered_vehicle_violation
      policyViolation: policy_violation
      transactionType: transaction_type
      eventType: event_type
      potentialRevenue: potential_revenue
      price
      revenue
      currency
      addressCity: address_city
      addressState: address_state
      addressStreet: address_street
      startTime: start_time
      endTime: end_time
      transactionDate: transaction_date
      transactionTs: transaction_ts
      transaction {
        index
        gatewayTransactions: gateway_transactions(limit: 1) {
          gatewayTransactionType: gateway_transaction_type
        }
        registeredVehicle: registered_vehicle {
          ...registeredVehicleFragment
        }
      }
    }
    aggregate: financial_transaction_log_no_balance_aggregate(
      where: {
        transaction_date: { _gte: $since, _lt: $until }
        transaction_type: { _eq: $transactionType }
        _or: [{ first_park: { _eq: true } }, $priceFilter]
        _and: [$filter]
      }
    ) {
      aggregate {
        count
      }
    }
  }
  ${registeredVehicleFragment}
`;

export interface GetCurbsuiteTransactionsData {
  transactions: CurbsuiteTransactionLogEntry[];
  aggregate: {
    aggregate: {
      count: number;
    };
  };
}

export interface CurbsuiteTransactionLogEntry {
  transactionId: string;
  firstPark: boolean;
  unregisteredVehicleViolation: boolean;
  policyViolation: boolean;
  transactionType: "park" | "gateway_transaction";
  eventType: "park" | "double_park" | "gateway_transaction";
  potentialRevenue: number;
  price: number;
  revenue: number;
  currency: string;
  addressCity: string | null;
  addressState: string | null;
  addressStreet: string | null;
  startTime: string | null;
  endTime: string | null;
  transactionTs: string;
  transactionDate: string;
  transaction: {
    index: number;
    gatewayTransactions: {
      gatewayTransactionType: "initial_wallet_load" | "wallet_reload" | "payout" | "wallet_refund";
    }[];
    registeredVehicle?: RegisteredVehicle;
  };
}

export type GatewayTransactionType = "initial_wallet_load" | "wallet_reload" | "payout" | "wallet_refund";

export interface GetCurbsuiteTransactionsVars {
  since: Date;
  until: Date;
  transactionType: "park" | "gateway_transaction";
  filter: GetCurbsuiteTransactionsFilter;
  priceFilter: typeof GET_PAYEE_TRANSACTIONS_PRICE_FILTER | typeof GET_PAYEE_TRANSACTIONS_POTENTIAL_REVENUE_FILTER;
  limit: number;
  offset: number;
  orderBy?: TransactionOrderBy[];
}

export interface GetCurbsuiteTransactionsFilter {
  transaction_type: { _eq: "gateway_transaction" } | { _eq: "park" };
  address_street?: { _ilike: string };
  transaction?:
    | {
        gateway_transactions: {
          gateway_transaction_type: { _eq: GatewayTransactionType };
        };
      }
    | { index: { _eq: number } };
}

export type TransactionOrderBy = TransactionOrderByIndex | TransactionOrderByTimeRange | TransactionOrderByDate;

export interface TransactionOrderByIndex {
  created_at: OrderBy;
}

export interface TransactionOrderByTimeRange {
  end_time: OrderBy;
}

export interface TransactionOrderByDate {
  date: OrderBy;
}
